import './Documentation.css';

import { Box, Grid } from '@material-ui/core';
import { DocumentationRequired, DocumentationRequiredType, PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useParams } from 'react-router-dom';

import { DocumentationHeader } from '@/components/modules/Documentation/components/DocumentationHeader/DocumentationHeader';
import { DocumentationRequiredCard } from '@/components/modules/Documentation/components/DocumentationRequiredCard';
import { DocumentationRequiredFiles } from '@/components/modules/Documentation/components/DocumentationRequiredFiles/DocumentationRequiredFiles';
import { useDocumentation } from '@/components/modules/Documentation/hooks/useDocumentation';
import { useParticipantParamsByType } from '@/components/modules/Documentation/hooks/useParticipantParamsByType';
import { DocumentationMode } from '@/components/modules/Documentation/types/DocumentationMode';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { ModuleCorePermissionProps } from '@/config/types';
import { useScreen } from '@/hooks/useScreen';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

interface DocumentationProps extends ModuleCorePermissionProps {
  paymentMethodType: PaymentMethodType;
}

const Documentation = ({ canEdit, paymentMethodType }: DocumentationProps) => {
  const { isMobile } = useScreen();
  const { loanId } = useParams();

  // TODO: For now, all roles that allow edit, will have the full mode
  const mode = canEdit ? DocumentationMode.FULL : DocumentationMode.VIEW_ONLY;

  if (!loanId) {
    return null;
  }

  const {
    allDocumentationRequired,
    selectedDocumentationRequired,
    setSelectedDocumentationRequired,
    selectedDocumentationRequiredType,
    loan,
  } = useDocumentation({ loanId });

  const { getParticipantCardParamsByType } = useParticipantParamsByType();

  if (!loanId || !loan || !selectedDocumentationRequired || !allDocumentationRequired) {
    return <PendingDataWarning />;
  }

  const selectedDocumentationCardParams = getParticipantCardParamsByType({
    currentLoan: loan,
    documentationRequiredType: selectedDocumentationRequiredType,
    documentationRequired: selectedDocumentationRequired,
  });

  const selectDocumentationRequired = (documentationRequired: DocumentationRequired) => {
    resetAnimation();
    setSelectedDocumentationRequired(documentationRequired);
  };

  const selectedBorrowerId =
    'borrowerId' in selectedDocumentationRequired ? selectedDocumentationRequired.borrowerId : undefined;

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} lg={6}>
        {!isMobile && (
          <Box pl={1} pr={2} mb={2}>
            <DocumentationHeader loan={loan} mode={mode} />
          </Box>
        )}
        <CustomPaper fill="grey.light" style={{ padding: 16, height: isMobile ? 'inherit' : '404px' }}>
          <Box className={isMobile ? 'documentation-required-container-mobile' : 'documentation-required-container'}>
            {allDocumentationRequired.borrowers &&
              allDocumentationRequired.borrowers
                .sort((a) => (a.borrowerId === loan.borrowerId ? -1 : 1))
                .map((documentationRequired, key) => (
                  <DocumentationRequiredCard
                    key={key}
                    currentLoan={loan}
                    documentationRequired={documentationRequired}
                    selectedDocumentationRequired={selectedDocumentationRequired}
                    selectDocumentationRequired={selectDocumentationRequired}
                    documentationRequiredType={DocumentationRequiredType.BORROWER}
                  />
                ))}
            {allDocumentationRequired.loan && (
              <DocumentationRequiredCard
                currentLoan={loan}
                documentationRequired={allDocumentationRequired.loan}
                selectedDocumentationRequired={selectedDocumentationRequired}
                selectDocumentationRequired={selectDocumentationRequired}
                documentationRequiredType={DocumentationRequiredType.LOAN}
              />
            )}
          </Box>
        </CustomPaper>
      </Grid>
      <Grid item xs={12} lg={6}>
        <DocumentationRequiredFiles
          selectedDocumentationCardParams={selectedDocumentationCardParams}
          selectedDocumentationRequired={selectedDocumentationRequired}
          selectedDocumentationRequiredType={selectedDocumentationRequiredType}
          paymentMethodId={loanId}
          paymentMethodType={paymentMethodType}
          borrowerId={selectedBorrowerId}
          mode={mode}
        />
      </Grid>
    </Grid>
  );
};

const resetAnimation = () => {
  const el = document.getElementById('resettableAnimation');
  if (el) {
    el.style.animation = 'none';
    el.offsetHeight; /* trigger reflow */
    el.style.animation = '';
  }
};

export const DocumentationModule = withPermissionsModule(Documentation);
